import { Box, BoxProps } from "@chakra-ui/react";
import type { ChartValue, DomChartDefinition } from "@tanstack/charts";
import {
  Chart,
  ChartTooltipBodyRenderContext,
} from "@tanstack/charts/react/tooltip";
import { ReactNode } from "react";
import { BrandChartTheme } from "./brandChartTheme";

type BrandChartProps<
  TDatum,
  TXValue extends ChartValue,
  TYValue extends ChartValue,
> = {
  definition: DomChartDefinition<TDatum, TXValue, TYValue>;
  brandTheme: BrandChartTheme;
  ariaLabel: string;
  ariaDescription?: string;
  renderTooltipBody?: (
    context: ChartTooltipBodyRenderContext<TDatum, TXValue, TYValue>,
  ) => ReactNode;
} & Pick<
  BoxProps,
  "width" | "height" | "minHeight" | "flexGrow" | "flexShrink"
>;

/**
 * TanStack Charts surface with intentsify.io typography and tooltip chrome.
 * Height comes from the container, so give it a height, min height or flex
 * size.
 */
export const BrandChart = <
  TDatum,
  TXValue extends ChartValue = ChartValue,
  TYValue extends ChartValue = ChartValue,
>({
  definition,
  brandTheme,
  ariaLabel,
  ariaDescription,
  renderTooltipBody,
  ...boxProps
}: BrandChartProps<TDatum, TXValue, TYValue>) => {
  return (
    <Box width="100%" style={brandTheme.containerStyle} {...boxProps}>
      <Chart<TDatum, TXValue, TYValue>
        definition={definition}
        ariaLabel={ariaLabel}
        ariaDescription={ariaDescription}
        renderTooltipBody={
          renderTooltipBody &&
          ((
            context: ChartTooltipBodyRenderContext<TDatum, TXValue, TYValue>,
          ) => (
            // A custom body replaces the default tooltip chrome, so restore it
            // from the same variables the default tooltip reads.
            <Box
              bg="var(--ts-chart-tooltip-background)"
              color="var(--ts-chart-tooltip-color)"
              border="var(--ts-chart-tooltip-border)"
              borderRadius="var(--ts-chart-tooltip-border-radius)"
              boxShadow="var(--ts-chart-tooltip-shadow)"
              px={3}
              py={2}
            >
              {renderTooltipBody(context)}
            </Box>
          ))
        }
        style={{ width: "100%", height: "100%" }}
      />
    </Box>
  );
};
